import type { Movie } from '@filmsloth/shared';
import { useMovieTitle, useT } from '../i18n/index.js';
import { movieMeta } from '../lib/movieMeta.js';
import { Poster } from '../components/Poster.js';
import { useSession } from '../session/useSession.js';

/**
 * The shortlist vote.
 *
 * A different question from swiping, and it looks different on purpose: one
 * choice, out of a handful, and you can change it until the last ballot lands.
 * Counts are visible while voting — this is a group in one room, not a secret
 * ballot, and watching the tally is what makes waiting for the last person
 * bearable.
 */
export function RunoffScreen() {
  const t = useT();
  const movieTitle = useMovieTitle();
  const { state, personal, isHost, voteRunoff, breakRunoffTie } = useSession();
  const runoff = state?.runoff;
  if (!runoff) return null;

  const yours = personal?.runoffVote ?? null;
  const votesFor = (movie: Movie) =>
    runoff.counts.find((entry) => entry.movieId === movie.id)?.votes ?? 0;
  const tieBreak = runoff.tiedCandidates.length > 0;

  return (
    <div className="screen">
      <h1>{t('runoff.title')}</h1>
      <p className="muted">{t('runoff.body')}</p>

      {tieBreak ? (
        <p className="banner">{isHost ? t('runoff.tieHost') : t('runoff.tieWaiting')}</p>
      ) : (
        <p className="muted small">
          {runoff.complete
            ? t('runoff.allIn')
            : t('runoff.outstanding', { count: runoff.outstanding })}
        </p>
      )}

      <ul className="runoff">
        {runoff.candidates.map((movie) => {
          const chosen = yours === movie.id;
          const tied = runoff.tiedCandidates.some((candidate) => candidate.id === movie.id);
          // During a tie-break only the host acts, and only on tied films.
          const actionable = tieBreak ? isHost && tied : true;

          return (
            <li key={movie.id}>
              <button
                type="button"
                className={`runoff__option${chosen ? ' runoff__option--chosen' : ''}${
                  tied ? ' runoff__option--tied' : ''
                }`}
                aria-pressed={chosen}
                disabled={!actionable}
                onClick={() => void (tieBreak ? breakRunoffTie(movie.id) : voteRunoff(movie.id))}
              >
                <Poster movie={movie} size="sm" />
                <span className="runoff__text">
                  <span className="runoff__title">{movieTitle(movie)}</span>
                  <span className="small muted">{movieMeta(movie, t)}</span>
                </span>
                <span
                  className="runoff__count"
                  aria-label={t('runoff.votes', { count: votesFor(movie) })}
                >
                  {votesFor(movie)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {yours && !tieBreak ? <p className="muted small center">{t('runoff.changeHint')}</p> : null}
    </div>
  );
}
